import React, { useEffect, useState, useRef, useCallback } from 'react';
import * as async from 'async';

import { useWindowSize } from '../../hooks/useWindowSize';
import * as PixiComponentImport from './pixi-component-wrapper';
import FreecodePlayerHeader from './FreecodePlayerHeader/freecodePlayerHeader';
import FreecodePlayerFooter from './FreecodePlayerFooter/freecodePlayerFooter';
import { AbsoluteSpinner } from '../Spinner/spinner';
import {
  FreecodePlayerBackground,
  FreecodePlayerContainer,
  CanvasContainer,
  CanvasHolder,
  FreecodeCanvas,
} from './styles';

const PixiComponent = PixiComponentImport.GetPixiComponent();

Math.clamp = function (value, min, max) {
  return Math.min(Math.max(value, min), max);
};

export function FreecodePlayer({ testImplementation, levels, sizeLimitByParent, isDarkMode, playByDefault }) {
  const [canvasDimensions, setCanvasDimensions] = useState({});
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [isRunButtonActive, setIsRunButtonActive] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isPopOut, setIsPopOut] = useState(false);

  const windowSize = useWindowSize();

  // instance variable refs
  const isPlayingRef = useRef(false);
  const stepTimeRef = useRef(testImplementation?.configCanvas?.stepTime ?? 400);
  const levelStepsRef = useRef(levels?.[0]?.steps);
  const gameIntervalRef = useRef(null);
  const gamePauseTimeRef = useRef(null);
  const startPlayingTimeRef = useRef(0);
  const currentStepRef = useRef(0);
  const pixelsPerUnitRef = useRef(null);

  // Elements refs
  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const footerRef = useRef(null);

  const calculateCanvasDimensions = useCallback(() => {
    if (!testImplementation) return;
    let canvasDimensionHeight = testImplementation.configCanvas.height;
    let canvasDimensionWidth = testImplementation.configCanvas.width;

    let windowWidth = windowSize.width || window.innerWidth;
    let windowHeight = windowSize.height || window.innerHeight;

    // default static size
    let maxWidth = windowWidth * 0.5;
    let maxHeight = windowHeight * 0.5;
    if (isPopOut) {
      // Canvas will expand and cover screen
      maxWidth = windowWidth * 0.9;
      maxHeight = windowHeight - 250;
    } else if (sizeLimitByParent) {
      // Player size is limited by size of players parent element
      maxWidth = containerRef.current.parentElement.offsetWidth;
      maxHeight =
        containerRef.current.parentElement.offsetHeight -
        headerRef.current.offsetHeight -
        footerRef.current.offsetHeight;
    }
    pixelsPerUnitRef.current = Math.min(maxWidth / canvasDimensionWidth, maxHeight / canvasDimensionHeight);

    setCanvasDimensions({
      width: pixelsPerUnitRef.current * canvasDimensionWidth,
      height: pixelsPerUnitRef.current * canvasDimensionHeight,
    });
  }, [windowSize, isPopOut, testImplementation, sizeLimitByParent]);

  useEffect(() => {
    calculateCanvasDimensions();
    return () => {
      // cleanup when leaving component
      clearInterval(gameIntervalRef.current);
      isPlayingRef.current = false;
    };
  }, [calculateCanvasDimensions]);

  useEffect(() => {
    if (!testImplementation || allLoaded) return;
    stepTimeRef.current = testImplementation.configCanvas?.stepTime ?? 400;

    PixiComponent.initialize(
      {
        resizeTo: window,
        resolution: 1,
        antialias: true,
        backgroundColor: testImplementation.configCanvas.background,
        view: document.getElementById('canvas'),
      },
      () => {
        if (isPlayingRef.current) {
          PixiComponent.setTween(Math.clamp((Date.now() - startPlayingTimeRef.current) / stepTimeRef.current, 0, 1));
          updateProgressBarFillRateWithRegardsToTime();
        }
      }
    );

    async.eachSeries(
      testImplementation.graphics,
      (graphicData, callback) => {
        addImageProcess(graphicData).then(function (loadedImage) {
          if (graphicData.jsonData) {
            loadedImage.sheetData = graphicData.jsonData;
          }
          PixiComponent.addPreloadedImage(loadedImage);
          callback();
        });
      },
      () => {
        setAllLoaded(true);
      }
    );
  }, [testImplementation, allLoaded]);

  const addImageProcess = (graphicData) => {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.name = graphicData.name;
      img.onload = function () {
        resolve(img);
      };
      img.onerror = reject;
      img.crossOrigin = 'anonymous';
      img.src = graphicData.url;
    });
  };

  useEffect(() => {
    if (allLoaded) {
      calculateCanvasDimensions();
      PixiComponent.setScaleAndResolution(null, pixelsPerUnitRef.current);
    }
  }, [allLoaded, calculateCanvasDimensions]);

  useEffect(() => {
    if (!allLoaded || !levels?.length) return;
    // All loaded or levels changed
    currentStepRef.current = 0;
    levelStepsRef.current = levels[0].steps;
    clearInterval(gameIntervalRef.current);
    setIsRunButtonActive(false);
    setSelectedLevel(0);
    PixiComponent.preprocessLevelOutput(levels, pixelsPerUnitRef.current);
    PixiComponent.setLevelAndStep(0, currentStepRef.current);
    PixiComponent.setTween(1);
    updateProgressBarFillRateWithRegardsToTime();
    if (playByDefault) setIsRunButtonActive(true);
  }, [allLoaded, levels, playByDefault]);

  // Used when updating progress with mouse
  const updateProgressBarByPercentage = useCallback(
    (percentage) => {
      if (levelStepsRef.current?.length <= 1) return;
      setProgressPercentage(100 * percentage);
      let realStep = percentage * (levelStepsRef.current.length - 2);
      let [step, stepDiff, realPart] = [0, 0, 0];

      if (realStep < 1) {
        step = 0;
        if (realStep === 0) {
          realPart = 0;
          stepDiff = -currentStepRef.current;
        } else {
          realPart = realStep;
          stepDiff = -currentStepRef.current + 1;
        }
      } else {
        step = Math.floor(realStep);
        if (step > levelStepsRef.current.length - 2) {
          step = levelStepsRef.current.length - 2;
        }
        if (step === levelStepsRef.current.length - 2) {
          realPart = 1;
          stepDiff = step - currentStepRef.current;
        } else {
          realPart = realStep % step;
          stepDiff = step - currentStepRef.current + 1;
        }
      }
      if (stepDiff !== 0) {
        currentStepRef.current += stepDiff;
        PixiComponent.setLevelAndStep(selectedLevel, currentStepRef.current);
      }

      PixiComponent.setTween(realPart);
    },
    [selectedLevel]
  );

  // Used for play and step forward
  const updateProgressBarFillRateWithRegardsToTime = () => {
    let t = Math.clamp((Date.now() - startPlayingTimeRef.current) / stepTimeRef.current, 0, 1);
    let x = (t + currentStepRef.current - 1) / (levelStepsRef.current.length - 2);
    setProgressPercentage(100 * x);
  };

  // Used for step backwards
  const updateProgressBarFillRateExplicitly = (part) => {
    let t = Math.clamp(part, 0, 1);
    let x = (t + currentStepRef.current - 1) / (levelStepsRef.current.length - 2);
    setProgressPercentage(100 * x);
  };

  const canPlayForward = useCallback(() => {
    var level = levels[selectedLevel];
    return currentStepRef.current < level.steps.length - 2;
  }, [levels, selectedLevel]);

  const pauseCanvasTimer = useCallback(
    (t) => {
      if (!isRunButtonActive) {
        clearTimeout(gamePauseTimeRef.current);
        gamePauseTimeRef.current = setTimeout(function () {
          isPlayingRef.current = false;
        }, t);
      }
    },
    [isRunButtonActive]
  );

  const stepForward = useCallback(() => {
    if (canPlayForward()) {
      currentStepRef.current += 1;
      isPlayingRef.current = true;
      startPlayingTimeRef.current = Date.now();
      PixiComponent.setLevelAndStep(selectedLevel, currentStepRef.current);
      pauseCanvasTimer(stepTimeRef.current);
    } else {
      setIsRunButtonActive(false);
      clearInterval(gameIntervalRef.current);
    }
  }, [canPlayForward, pauseCanvasTimer, selectedLevel]);

  const stepBackward = () => {
    if (currentStepRef.current >= 1) {
      currentStepRef.current -= 1;
      PixiComponent.setLevelAndStep(selectedLevel, currentStepRef.current);
      startPlayingTimeRef.current = Date.now() + stepTimeRef.current;
      PixiComponent.setTween(1);
      updateProgressBarFillRateExplicitly(1);
    }
  };

  const resetLevels = useCallback(() => {
    levelStepsRef.current = levels[selectedLevel].steps;
    PixiComponent.setLevelAndStep(selectedLevel, currentStepRef.current);
    PixiComponent.setTween(1);
    updateProgressBarFillRateWithRegardsToTime();
  }, [levels, selectedLevel]);

  useEffect(() => {
    if (allLoaded) {
      levelStepsRef.current = levels[selectedLevel].steps;
      PixiComponent.preprocessLevelOutput(levels, pixelsPerUnitRef.current);
      PixiComponent.setLevelAndStep(selectedLevel, currentStepRef.current);
      PixiComponent.setTween(1);
      updateProgressBarFillRateWithRegardsToTime();
    }
  }, [allLoaded, selectedLevel, levels]);

  useEffect(() => {
    if (isRunButtonActive) {
      if (!canPlayForward()) {
        currentStepRef.current = 0;
        resetLevels();
      }
      clearTimeout(gamePauseTimeRef.current);
      gameIntervalRef.current = setInterval(() => {
        stepForward();
      }, stepTimeRef.current);
    } else {
      pauseCanvasTimer(0);
      clearInterval(gameIntervalRef.current);
    }
  }, [isRunButtonActive, canPlayForward, pauseCanvasTimer, resetLevels, stepForward]);

  const selectLevel = (index) => {
    if (selectedLevel !== index) {
      clearInterval(gameIntervalRef.current);
      pauseCanvasTimer(0);
      startPlayingTimeRef.current = 0;
      currentStepRef.current = 0;
      setIsRunButtonActive(false);
      setSelectedLevel(index);
    }
  };

  const renderFreecodePlayerHeader = () => {
    const tabValues = !allLoaded
      ? [{ label: 'Loading...' }]
      : levels.map((item, index) => ({ label: 'Level ' + (index + 1) }));
    return (
      <FreecodePlayerHeader
        ref={headerRef}
        isDarkMode={isDarkMode}
        items={tabValues}
        active={selectedLevel}
        setActive={selectLevel}
        isPopOut={isPopOut}
        setIsPopOut={setIsPopOut}
      />
    );
  };

  const renderFreecodePlayerFooter = () => (
    <FreecodePlayerFooter
      ref={footerRef}
      isDarkMode={isDarkMode}
      loading={!allLoaded}
      testImplementation={testImplementation}
      isRunButtonActive={isRunButtonActive}
      setIsRunButtonActive={setIsRunButtonActive}
      stepForward={stepForward}
      stepBackward={stepBackward}
      progressPercentage={progressPercentage}
      updateProgressBarByPercentage={updateProgressBarByPercentage}
    />
  );

  return (
    <FreecodePlayerBackground ref={containerRef} isPopOut={isPopOut}>
      <FreecodePlayerContainer id="freecodePlayer" isPopOut={isPopOut} isDarkMode={isDarkMode}>
        {renderFreecodePlayerHeader()}
        <AbsoluteSpinner
          width={canvasDimensions.width + 'px'}
          height={canvasDimensions.height + 'px'}
          loading={!allLoaded}
          backgroundOverlay
        />
        <CanvasContainer isDarkMode={isDarkMode}>
          <CanvasHolder height={canvasDimensions.height} width={canvasDimensions.width}>
            <FreecodeCanvas id="canvas" />
          </CanvasHolder>
        </CanvasContainer>

        {renderFreecodePlayerFooter()}
      </FreecodePlayerContainer>
    </FreecodePlayerBackground>
  );
}
